import { useCallback, useEffect, useMemo, useState } from 'react';

const normalizePayload = (values = {}) => {
  const payload = {
    plu1: (values.plu1 || '').trim(),
    nombre: (values.nombre || '').trim(),
    articulo_id: values.articulo_id ? Number(values.articulo_id) : null,
    proceso_id: values.proceso_id ? Number(values.proceso_id) : null,
    activo: Boolean(values.activo),
    imagen_url: values.imagen_url || null
  };

  ['plu2', 'plu3', 'plu4', 'plu5', 'plu6'].forEach((field) => {
    const value = values[field];
    if (value && value.trim() !== '') {
      payload[field] = value.trim();
    }
  });

  if (!payload.plu1 || !payload.nombre) {
    throw new Error('PLU1 y nombre son requeridos.');
  }

  if (Object.prototype.hasOwnProperty.call(values, 'tiempo_vida_minutos')) {
    const rawMinutes = values.tiempo_vida_minutos;
    if (rawMinutes === '' || rawMinutes === null || typeof rawMinutes === 'undefined') {
      payload.tiempo_vida_minutos = null;
    } else {
      const minutes = Number(rawMinutes);
      if (!Number.isFinite(minutes) || minutes < 0) {
        throw new Error('El tiempo de vida debe ser un número válido.');
      }
      payload.tiempo_vida_minutos = Math.max(0, Math.round(minutes));
    }
  }

  if (values.warmer_minutes && typeof values.warmer_minutes === 'object') {
    const clean = {};
    Object.entries(values.warmer_minutes).forEach(([warmerId, minutes]) => {
      if (minutes === '' || minutes === null || typeof minutes === 'undefined') {
        return;
      }
      const idNumber = Number(warmerId);
      const minutesNumber = Number(minutes);
      if (Number.isFinite(idNumber) && Number.isFinite(minutesNumber)) {
        clean[idNumber] = Math.max(0, Math.round(minutesNumber));
      }
    });
    if (Object.keys(clean).length > 0) {
      payload.warmer_minutes = clean;
    }
  }

  return payload;
};

const mapProcesses = (payload = []) =>
  payload
    .filter(Boolean)
    .map((process) => {
      const id = process.id ?? process.proceso_id ?? null;
      if (!id) {
        return null;
      }
      return {
        id,
        nombre: process.nombre || process.name || 'Sin nombre',
        es_warmer: Boolean(process.es_warmer)
      };
    })
    .filter(Boolean);

const mapArticles = (payload = []) =>
  payload
    .filter(Boolean)
    .map((article) => {
      const id = article.id ?? article.articulo_id ?? article.article_id ?? null;
      if (!id) {
        return null;
      }
      const rawMinutes = article.tiempo_vida_minutos;
      const minutes = Number(rawMinutes);
      return {
        id,
        nombre: article.nombre || article.name || 'Sin nombre',
        tiempo_vida_minutos: Number.isFinite(minutes) ? minutes : null
      };
    })
    .filter(Boolean);

const mapWarmers = (payload = []) =>
  payload
    .filter(Boolean)
    .map((warmer) => {
      const id = warmer.id ?? warmer.warmer_id ?? null;
      if (!id) {
        return null;
      }
      return {
        id,
        nombre: warmer.nombre || warmer.name || `Warmer ${id}`,
        activo: warmer.activo ?? true
      };
    })
    .filter(Boolean);

export const useAdminRecipes = () => {
  const [items, setItems] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [articles, setArticles] = useState([]);
  const [warmers, setWarmers] = useState([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [optionsError, setOptionsError] = useState(null);
  const [optionsToken, setOptionsToken] = useState(0);
  const [processFilter, setProcessFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);

  const fetchRecipes = useCallback(async (signal) => {
    setLoading(true);
    setError(null);
    try {
      const query = processFilter ? `?process_id=${processFilter}` : '';
      const response = await fetch(`/admin/api/recipes${query}`, { signal });
      const data = await response.json();
      if (!response.ok || data?.error) {
        throw new Error(data?.error || 'No se pudieron obtener las recetas');
      }
      if (!signal?.aborted) {
        const normalized = (data.recipes || []).map((recipe) => ({
          ...recipe,
          warmers: recipe.warmers || []
        }));
        setItems(normalized);
      }
    } catch (err) {
      if (!signal?.aborted) {
        setError(err?.message || 'Error al cargar recetas');
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, [processFilter]);

  useEffect(() => {
    const controller = new AbortController();
    fetchRecipes(controller.signal);
    return () => controller.abort();
  }, [fetchRecipes, refreshToken]);

  const fetchProcesses = useCallback(async (signal) => {
    setOptionsLoading(true);
    setOptionsError(null);
    const readJson = async (response) => {
      try {
        const contentType = response.headers.get('content-type') || '';
        if (!contentType.includes('application/json')) {
          throw new Error('Respuesta inesperada del servidor (no es JSON).');
        }
        return await response.json();
      } catch (err) {
        console.warn('No se pudo parsear la respuesta de recetas/artículos:', err?.message || err);
        return null;
      }
    };

    try {
      const [articulosResponse, recetasResponse, warmersResponse] = await Promise.all([
        fetch('/admin/api/articles?per_page=1000', { signal }),
        fetch('/admin/api/processes/recetas', { signal }),
        fetch('/admin/api/warmers?active_only=1', { signal })
      ]);
      const [articulosData, recetasData, warmersData] = await Promise.all([
        readJson(articulosResponse),
        readJson(recetasResponse),
        readJson(warmersResponse)
      ]);
      if (signal?.aborted) {
        return;
      }
      const errorMessages = [];

      const mappedProcesses = mapProcesses(recetasData?.processes || recetasData?.items || []);
      if (recetasResponse.ok && !recetasData?.error && mappedProcesses.length > 0) {
        setProcesses(mappedProcesses);
      } else {
        errorMessages.push(recetasData?.error || 'No se pudieron obtener los procesos de recetas.');
      }

      const mappedArticles = mapArticles(articulosData?.articles || articulosData?.items || []);
      if (articulosResponse.ok && !articulosData?.error && mappedArticles.length > 0) {
        setArticles(mappedArticles);
      } else {
        if (articulosResponse.ok && mappedArticles.length === 0) {
          errorMessages.push('No se encontraron artículos disponibles para vincular.');
        } else {
          errorMessages.push(articulosData?.error || 'No se pudieron obtener los artículos.');
        }
      }

      const mappedWarmers = mapWarmers(warmersData?.warmers || warmersData?.items || []);
      if (warmersResponse.ok && !warmersData?.error) {
        setWarmers(mappedWarmers);
      } else if (!warmersResponse.ok) {
        errorMessages.push(warmersData?.error || 'No se pudieron obtener los warmers.');
      }

      if (errorMessages.length > 0) {
        setOptionsError(errorMessages.join(' '));
      }
    } catch (err) {
      if (!signal?.aborted) {
        console.warn('No se pudieron cargar procesos/artículos/warmers:', err.message);
        setOptionsError(err?.message || 'No se pudieron cargar los procesos, artículos o warmers.');
      }
    } finally {
      if (!signal?.aborted) {
        setOptionsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchProcesses(controller.signal);
    return () => controller.abort();
  }, [fetchProcesses, optionsToken]);

  const refresh = useCallback(() => {
    setRefreshToken((value) => value + 1);
  }, []);

  const refreshOptions = useCallback(() => {
    setOptionsToken((value) => value + 1);
  }, []);

  const saveRecipe = useCallback(async (values) => {
    const payload = normalizePayload(values);
    const hasId = Boolean(values?.id);
    const url = hasId ? `/admin/api/recipes/${values.id}` : '/admin/api/recipes';
    const method = hasId ? 'PUT' : 'POST';
    const response = await fetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo guardar la receta');
    }
    return data;
  }, []);

  const deleteRecipe = useCallback(async (id) => {
    const response = await fetch(`/admin/api/recipes/${id}`, { method: 'DELETE' });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo eliminar la receta');
    }
    return data;
  }, []);

  const uploadImage = useCallback(async ({ file, entityId }) => {
    if (!file) {
      throw new Error('Selecciona un archivo válido.');
    }
    const formData = new FormData();
    formData.append('image', file);
    formData.append('entity_type', 'recipe');
    if (entityId) {
      formData.append('entity_id', entityId);
    }
    const response = await fetch('/admin/api/upload-image', {
      method: 'POST',
      body: formData
    });
    const data = await response.json();
    if (!response.ok || data?.error) {
      throw new Error(data?.error || 'No se pudo subir la imagen');
    }
    if (entityId) {
      const id = Number(entityId);
      setItems((prev) => prev.map((item) => (
        item.id === id ? { ...item, imagen_url: data.image_url } : item
      )));
    }
    return data;
  }, []);

  return useMemo(() => ({
    items,
    processes,
    articles,
    warmers,
    optionsLoading,
    optionsError,
    processFilter,
    setProcessFilter,
    loading,
    error,
    refresh,
    refreshOptions,
    saveRecipe,
    deleteRecipe,
    uploadImage
  }), [
    items,
    processes,
    articles,
    warmers,
    optionsLoading,
    optionsError,
    processFilter,
    loading,
    error,
    refresh,
    refreshOptions,
    saveRecipe,
    deleteRecipe,
    uploadImage
  ]);
};
